// ©2024 Austin App House. All rights reserved.
import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { connect } from 'react-redux';
import { toggleSecondModalClose } from '../../storage/actions/modalsActions';
import Loader from '../dashboard/common/Loader';
import Notification from '../notification';

const ExplanationInputModal = ({
  toggleSecondModalClose,
  returnExplanation,
  title,
  modalState,
  type,
  isCloseAvailable = false,
  useOutOfState = false,
  warningMessage = 'Please enter valid reason to unassign the contractor.',
  isOpen = false,
  changeIsOpen,
}) => {
  const [message, setMessage] = useState('');
  const [showLoader, setShowLoader] = useState(false);

  const [show, setShow] = useState(isOpen);

  const resolvedIsOpen = isOpen || show;
  const resolvedChangeIsOpen = changeIsOpen || setShow;

  const showModal = useMemo(() => {
    if (useOutOfState) return resolvedIsOpen;
    if (!modalState?.isOpen) return false;
    if (type && modalState?.type && modalState.type !== type) return false;
    return true;
  }, [modalState, resolvedIsOpen, useOutOfState, type]);

  useEffect(() => {
    if (!showModal) {
      setShowLoader(false);
    }
  }, [showModal]);

  const closeModal = () => {
    if (!isCloseAvailable || showLoader) return;

    setMessage('');
    setShowLoader(false);

    if (useOutOfState) {
      resolvedChangeIsOpen(false);
      return;
    }

    toggleSecondModalClose('explanationInputModal', true);
  };

  if (!showModal) return null;

  return ReactDOM.createPortal(
    <div
      className="modal_block dashboard-modal dashboard-modal--centered modal_explanation showed"
      role="presentation"
      onClick={closeModal}
    >
      <div
        className="modal_container"
        role="dialog"
        aria-modal="true"
        aria-labelledby="explanation-input-title"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="modal_head">
          <div className="modal_title" id="explanation-input-title">
            {title}
          </div>
          {isCloseAvailable && (
            <button
              type="button"
              className="close_btn"
              aria-label="close"
              onClick={closeModal}
            >
              ×
            </button>
          )}
        </div>
        <div className="modal_body">
          {showLoader ? (
            <div className="flex-box flex-center">
              <Loader />
            </div>
          ) : (
            <div className="fields_group">
              <div className="field_row">
                <div className="field_name">
                  <label htmlFor="reason_to_unroute">Please Type the Reason</label>
                </div>
                <div className="field_block">
                  <label className="explanation-input-note">
                    (This message will be sent to the contractor)
                  </label>
                  <input
                    type="text"
                    className="form-control dashboard-input"
                    id="reason_to_unroute"
                    value={message}
                    onChange={({ target }) => setMessage(target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && message.trim()) {
                        returnExplanation(message);
                        setMessage('');
                        setShowLoader(true);
                      }
                    }}
                    required
                  />
                </div>
              </div>
            </div>
          )}
        </div>
        {!showLoader && (
          <div className="modal_footer">
            <button
              type="button"
              className="dashboard-btn dashboard-btn--primary"
              aria-label="submit"
              onClick={() => {
                if (!message.trim()) {
                  Notification('warning', {
                    message: warningMessage,
                  });
                } else {
                  returnExplanation(message);
                  setMessage('');
                  setShowLoader(true);
                }
              }}
            >
              Submit
            </button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
};

const mapStateToProps = state => ({
  modalState: state.modalState.explanationInputModal,
});

export default connect(mapStateToProps, { toggleSecondModalClose })(ExplanationInputModal);
